import { computed, inject, Injector } from '@angular/core';
import { catchError, EMPTY, Observable, pipe, tap } from 'rxjs';
import { patchState, signalStore, type, withComputed, withMethods, withState } from '@ngrx/signals';
import { entityConfig, setAllEntities, updateEntities, updateEntity, withEntities } from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { withCallStatus } from '@ngrx-traits/signals';

import { FormlySelectOption } from '@ngx-formly/core/select';

import { calcColumns } from '../utils';

type Option = FormlySelectOption & {
  active?: boolean;
};

type MultipleCheckboxArrayState = {
  initialVisibleOptionsCount: number;
  visibleOptionsCount: number;
  searchEnabled: boolean;
  search: string;
};

export const DEFAULT_VISIBLE_OPTIONS_COUNT = 20 as const;

const initialState: MultipleCheckboxArrayState = {
  initialVisibleOptionsCount: DEFAULT_VISIBLE_OPTIONS_COUNT,
  visibleOptionsCount: DEFAULT_VISIBLE_OPTIONS_COUNT,
  search: '',
  searchEnabled: false,
};

const optionsConfig = entityConfig({
  entity: type<Option>(),
  selectId: o => o.value,
});

export const MultipleSelectStore = signalStore(
  withState(initialState),
  withEntities<Option>(optionsConfig),
  withCallStatus({ prop: 'options' }),
  withComputed(({ entities, visibleOptionsCount, search }) => {
    const totalOptionsCount = computed(() => entities().length);

    const columns = computed(() => calcColumns(totalOptionsCount()));

    const filteredOptions = computed(() =>
      entities().filter(o => o.label?.toLowerCase().includes(search().toLowerCase()))
    );

    const visibleOptions = computed(() => filteredOptions().slice(0, visibleOptionsCount()));

    const hasMoreOptions = computed(() => filteredOptions().length > visibleOptionsCount());

    const selectedOptions = computed(() => entities().filter(e => e.active));

    const selectedValues = computed(() => selectedOptions().map(o => o.value));

    const everySelected = computed(() => entities().every(e => e.active));

    const someSelected = computed(() => !everySelected() && entities().some(e => e?.active));

    return {
      columns,
      everySelected,
      hasMoreOptions,
      filteredOptions,
      selectedOptions,
      selectedValues,
      someSelected,
      totalOptionsCount,
      visibleOptions,
    };
  }),
  withMethods((store, injector = inject(Injector)) => {
    const setInitialVisibleOptionsCount = (count: number) => patchState(store, {
      initialVisibleOptionsCount: count ?? DEFAULT_VISIBLE_OPTIONS_COUNT,
      visibleOptionsCount: count ?? DEFAULT_VISIBLE_OPTIONS_COUNT
    });

    const setOptions = (options: Observable<FormlySelectOption[]>, selectedOptions: any | any[]) => {
      store.setOptionsLoading();
      return rxMethod<Option[]>(
        pipe(
          tap(options => {
            const opts = Array.isArray(selectedOptions) ? selectedOptions : [selectedOptions];
            patchState(
              store,
              setAllEntities(
                (options ?? []).map(o => ({ ...o, active: o.active ?? opts.includes(o.value) } as Option)),
                optionsConfig
              )
            );

            store.setOptionsLoaded();

            if (!selectedOptions) return;
          }),
          catchError(error => {
            store.setOptionsError(error);
            return EMPTY;
          })
        ),
        { injector }
      )(options);
    };

    const showMoreOptions = () =>
      patchState(
        store,
        state => ({ visibleOptionsCount: state.visibleOptionsCount + DEFAULT_VISIBLE_OPTIONS_COUNT })
      );

    const showLessOptions = () => patchState(
      store,
      state => ({
        visibleOptionsCount: state.initialVisibleOptionsCount || state.visibleOptionsCount || DEFAULT_VISIBLE_OPTIONS_COUNT
      })
    );

    const toggleOption = (value: any | undefined) => {
      patchState(
        store,
        updateEntity(
          {
            id: value,
            changes: (opt: Option) => ({ active: !opt.active }),
          },
          optionsConfig
        )
      );
    };

    const toggleAllOptions = (state?: boolean) => {
      patchState(
        store,
        updateEntities(
          {
            ids: store.filteredOptions().map(o => o.value),
            changes: (opt: Option) => ({ active: typeof state === 'boolean' ? state : !opt.active }),
          },
          optionsConfig
        )
      );
    };

    const toggleSearchBar = () =>
      patchState(store, state => ({
        searchEnabled: !state.searchEnabled,
        ...(state.searchEnabled ? { search: '' } : {}),
      }));

    const searchOptions = (input: string) => patchState(store, { search: input });

    return {
      searchOptions,
      setOptions,
      showLessOptions,
      showMoreOptions,
      toggleAllOptions,
      toggleOption,
      toggleSearchBar,
      setInitialVisibleOptionsCount,
    };
  })
);
